<template>
    <div class="limits-table">
        <table>
            <tr v-for="item in items" :key="item.id">
                <td>
                    <span class="limits-table__title typographies-paragraph-medium-semibold">
                        {{ item.title }}
                    </span>
                    <span class="limits-table__description typographies-paragraph-small-regular">
                        {{ item.description }}
                    </span>
                </td>
                <td class="typographies-paragraph-medium-regular">
                    <div class="limits-used">
                        {{
                            `${formatNumericValue(item.used, item)} / ${formatNumericValue(
                                item.limit,
                                item,
                                [
                                    'file_storage',
                                    'document_invoices',
                                    'bank_account_reconciliations',
                                ],
                                true,
                            )}`
                        }}
                        <CompassTooltip v-if="item.id === 'document_invoices'">
                            <template #content>
                                {{ $l10n.__('Remise à zéro le ') }}
                                {{ invoicingLimitResetDate }}
                            </template>
                            <CompassIcon class="info-icon" size="small" icon="info-circle-fill" />
                        </CompassTooltip>
                    </div>
                </td>
                <td>
                    <CompassTag
                        v-if="displayTag(item)"
                        type="colored"
                        size="small"
                        :color="displayTag(item)"
                    >
                        {{ tagLabel(item) }}
                    </CompassTag>
                </td>
                <td>
                    <span :class="remainderClass(item)">
                        {{
                            $l10n.n__(
                                '%(rest)s restant',
                                '%(rest)s restants',
                                remainingItems(item),
                                {
                                    rest: formatNumericValue(remainingItems(item), item, [
                                        'file_storage',
                                    ]),
                                },
                            )
                        }}
                    </span>
                </td>
            </tr>
        </table>
    </div>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';
const { mapState } = createNamespacedHelpers('subscriptionManager');
export default {
    name: 'LimitsAndCreditsTable',
    props: {
        items: {
            type: Array,
            required: true,
        },
        type: {
            type: String,
            default: 'quota',
            validator: (value) => ['quota', 'limit'].includes(value),
        },
    },
    data() {
        return {
            customFormatNumber: {
                thousands: ' ',
                precision: 0,
            },
        };
    },
    computed: {
        ...mapState(['serviceStartDate']),
        remainingItems() {
            return (item) => item.limit - item.used;
        },
        formatNumber() {
            return {
                ...this.$store.state.corp.numformat,
                ...this.customFormatNumber,
            };
        },
        remainderClass() {
            return (item) => {
                const classes = ['typographies-paragraph-medium-semibold'];
                if (item.limit - item.used <= 1) {
                    classes.push('remaining-items--red');
                }

                return classes;
            };
        },
        displayTag() {
            return (item) => {
                if (
                    (this.type === 'limit' && item.limit - item.used <= 0) ||
                    (this.type === 'quota' && item.limit - item.used <= 1)
                ) {
                    return 'red';
                }
                if (item.used / item.limit >= 0.8) {
                    return 'yellow';
                }
                return null;
            };
        },
        tagLabel() {
            return (item) => {
                if (this.displayTag(item) === 'yellow') {
                    return this.type === 'quota'
                        ? this.$l10n.__('Crédits à surveiller')
                        : this.$l10n.__('Limite presque atteinte');
                }
                return this.type === 'quota'
                    ? this.$l10n.__('Crédits épuisés ou presque')
                    : this.$l10n.__('Limite atteinte ou dépassée');
            };
        },
        formatNumericValue() {
            return (value, item, itemsWithUnits = [], showUnits = false) => {
                if (item.id === 'file_storage') {
                    const fileValue = this.$filters.numberFormatWithOptions(
                        value / 1e6,
                        this.formatNumber,
                    );

                    return itemsWithUnits.includes('file_storage')
                        ? `${fileValue} ${item.unit}`
                        : fileValue;
                }
                const fileValue = this.$filters.numberFormatWithOptions(value, this.formatNumber);
                return itemsWithUnits.includes(item.id) || (showUnits && this.type === 'quota')
                    ? `${fileValue} ${
                          item.limit > 1 && item.unitPluralized ? item.unitPluralized : item.unit
                      }`
                    : fileValue;
            };
        },
        invoicingLimitResetDate() {
            moment.locale(window.App.lang);
            const startDate = moment(this.serviceStartDate);
            const today = moment();

            let date;
            const time = today.format('LT');
            const separator = this.$l10n.__('à');

            if (today.get('date') < startDate.get('date')) {
                // reset date is this month
                date = moment({
                    years: today.get('year'),
                    months: today.get('month'),
                    date: startDate.get('date'),
                }).format('LL');
            } else {
                // reset date is next month
                date = moment({
                    years: today.get('year'),
                    months: today.get('month') + 1,
                    date: startDate.get('date'),
                }).format('LL');
            }
            return `${date} ${separator} ${time}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.limits-table {
    margin: var(--spacing-3) 0 0;

    table {
        border: 0;
        border-collapse: collapse;
        width: 100%;

        td {
            border-top: 1px solid var(--colors-common-border-default);
            color: var(--colors-common-text-standard-default);
            padding: 10px var(--spacing-3);

            &:first-child {
                width: 34%;
            }

            &:nth-child(3) {
                width: 25%;
            }

            &:last-child {
                width: 19%;
            }
        }

        tr {
            &:last-child {
                td {
                    border-bottom: 1px solid var(--colors-common-border-default);
                }
            }
        }
    }

    &__title {
        display: block;
    }

    &__description {
        color: var(--colors-common-text-subtle-default);
        display: block;
    }

    .limits-used {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
    }

    .remaining-items--red {
        color: var(--colors-common-text-alert-default);
    }

    .info-icon {
        fill: var(--colors-common-icon-neutral);
    }
}
</style>
